import React, { useState, useEffect } from 'react';
import notificationApi from '../../api/notificationApi';
import { formatDistanceToNow } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import {
    Bell,
    CheckCircle,
    Clock,
    ArrowLeft
} from 'lucide-react';
import { useSocket } from '../../context/SocketContext';
import PaginationControls from '../../components/common/Pagination/PaginationControls';

const Notifications = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        limit: 10,
        hasNextPage: false,
        hasPrevPage: false
    });
    const socket = useSocket();

    const fetchNotifications = async (page = currentPage, pageLimit = limit) => {
        try {
            setLoading(true);
            const response = await notificationApi.getNotifications({ page, limit: pageLimit });
            if (response.status === 'success') {
                setNotifications(response.data);
                if (response.pagination) {
                    setPagination(response.pagination);
                }
            }
        } catch (error) {
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchNotifications(currentPage, limit);
    }, [currentPage, limit]);

    useEffect(() => {
        if (socket) {
            const handleNewNotification = (notification) => {
                // Jump to page 1 and re-fetch so counts/pages stay accurate
                setCurrentPage(1);
            };
            const handleNotificationRead = ({ notificationId }) => {
                setNotifications(prev =>
                    prev.map(n => n._id === notificationId ? { ...n, isRead: true } : n)
                );
            };
            const handleAllNotificationsRead = () => {
                setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
            };

            socket.on('new-notification', handleNewNotification);
            socket.on('notification-read', handleNotificationRead);
            socket.on('notifications-all-read', handleAllNotificationsRead);

            return () => {
                socket.off('new-notification', handleNewNotification);
                socket.off('notification-read', handleNotificationRead);
                socket.off('notifications-all-read', handleAllNotificationsRead);
            };
        }
    }, [socket]);

    const handleMarkAsRead = async (notificationId) => {
        try {
            await notificationApi.markAsRead(notificationId);
            // State update will be handled by WebSocket 'notification-read' event
        } catch (error) {
        }
    };

    const handleMarkAllAsRead = async () => {
        try {
            await notificationApi.markAllAsRead();
            // State update will be handled by WebSocket 'notifications-all-read' event
        } catch (error) {
        }
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleLimitChange = (newLimit) => {
        setLimit(newLimit);
        setCurrentPage(1);
    };

    return (
        <div className="min-h-screen bg-[#F8FAFC] py-8">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header Section */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
                    <div>
                        <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight">Notifications</h1>
                        <p className="text-gray-500 mt-1 font-medium">Track system activities</p>
                    </div>

                    {notifications.some(n => !n.isRead) && (
                        <button
                            onClick={handleMarkAllAsRead}
                            className="flex items-center gap-2 px-5 py-2.5 bg-white border border-gray-200 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 hover:border-gray-300 transition-all shadow-sm group"
                        >
                            <CheckCircle className="w-4 h-4 text-[#22C55E] group-hover:scale-110 transition-transform" />
                            Mark all read
                        </button>
                    )}
                </div>

                {/* Content Section */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                    {loading && notifications.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-20">
                            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#22C55E] mb-4"></div>
                            <p className="text-gray-500 font-medium">Loading your notifications...</p>
                        </div>
                    ) : notifications.length > 0 ? (
                        <div className="divide-y divide-gray-50">
                            {notifications.map((notification) => {
                                const handleCardClick = async () => {
                                    // Mark as read if not already
                                    if (!notification.isRead) {
                                        await handleMarkAsRead(notification._id);
                                    }
                                    // Navigate to link if available (use React Router for smooth navigation)
                                    if (notification.link) {
                                        // Extract relative path from full URL if needed
                                        const url = notification.link;
                                        const relativePath = url.startsWith('http')
                                            ? new URL(url).pathname
                                            : url;
                                        navigate(relativePath);
                                    }
                                };

                                return (
                                    <div
                                        key={notification._id}
                                        onClick={handleCardClick}
                                        className={`group p-6 transition-all hover:bg-[#F8FAFC] flex gap-4 cursor-pointer ${!notification.isRead ? 'bg-[#F0FDF4]/50' : ''}`}
                                    >
                                        {/* Status Indicator Icon */}
                                        <div className={`mt-1 flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-transform group-hover:scale-110 ${!notification.isRead
                                            ? 'bg-[#22C55E] text-white shadow-lg shadow-green-200'
                                            : 'bg-gray-100 text-gray-400'
                                            }`}>
                                            <Bell className="w-5 h-5" />
                                        </div>

                                        {/* Notification Content */}
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-start justify-between gap-3 mb-1">
                                                <h4 className={`text-base font-bold truncate ${!notification.isRead ? 'text-gray-900' : 'text-gray-600'}`}>
                                                    {notification.title}
                                                </h4>
                                                <span className="text-xs text-gray-400 font-medium whitespace-nowrap flex items-center gap-1.5 bg-gray-50 px-2 py-1 rounded-md">
                                                    <Clock className="w-3 h-3" />
                                                    {formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true })}
                                                </span>
                                            </div>

                                            <p className={`text-sm leading-relaxed ${!notification.isRead ? 'text-gray-700 font-medium' : 'text-gray-500'}`}>
                                                {notification.message}
                                            </p>
                                        </div>

                                        {/* Unread dot */}
                                        {!notification.isRead && (
                                            <div className="flex-shrink-0 mt-2">
                                                <div className="w-2.5 h-2.5 bg-[#22C55E] rounded-full animate-pulse shadow-sm shadow-green-500"></div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="text-center py-24 px-6">
                            <div className="w-20 h-20 bg-[#F8FAFC] rounded-3xl flex items-center justify-center mx-auto mb-6 rotate-6 group-hover:rotate-0 transition-transform">
                                <Bell className="w-10 h-10 text-gray-300" />
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-2">You're all caught up!</h3>
                            <p className="text-gray-500 max-w-sm mx-auto">
                                There are no new system notifications for you at the moment. We'll alert you when something important happens.
                            </p>
                        </div>
                    )}
                </div>

                {/* Pagination */}
                <PaginationControls
                    currentPage={pagination.currentPage}
                    totalPages={pagination.totalPages}
                    totalItems={pagination.totalItems}
                    limit={pagination.limit}
                    hasNextPage={pagination.hasNextPage}
                    hasPrevPage={pagination.hasPrevPage}
                    onPageChange={handlePageChange}
                    onLimitChange={handleLimitChange}
                />
            </div>
        </div>
    );
};

export default Notifications;
